import React, { useState, useEffect, useMemo, useRef } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Spinner,
  FormGroup,
  Badge,
  Form,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  UncontrolledTooltip,
  Alert,
} from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import axios from "axios"
import apiURL from "url" // same as your project import
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { Link } from "react-router-dom"

import DatePicker from "react-datepicker"
import Select from "react-select"
import ExcelDownload from "components/Common/ExcelDownload"

const AllContactLeads = () => {
  document.title = "CRM Leads"

  // Core states
  const [addLeadModal, setAddLeadModal] = useState(false)
  const [form, setForm] = useState({
    name: "",
    contact: "",
    email: "",
    city: "",
    service_need: "",
    notes: "",
  })
  const [leads, setLeads] = useState([])
  const [selectedLead, setSelectedLead] = useState(null)
  const [loading, setLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(false)
  const role = (localStorage.getItem("role") || "").toLowerCase()
  const canChangeStatus = ["super_admin", "admin", "manager"].includes(role)
  const canDelete = ["super_admin", "admin"].includes(role)
  const assignedBy = localStorage.getItem("login_user_id")

  // Filter states
  const [filterOpen, setFilterOpen] = useState(false)
  const [filters, setFilters] = useState({
    status: "",
    service_need: "",
    city: "",
    startDate: null,
    endDate: null,
  })

  // Assignment states
  const [selectedLeadIds, setSelectedLeadIds] = useState([])
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [singleLeadId, setSingleLeadId] = useState(null)
  const [isAssigning, setIsAssigning] = useState(false)

  // Users and Remarks states
  const [users, setUsers] = useState([])
  const [usersCities, setUsersCities] = useState([])
  const [remarksModal, setRemarksModal] = useState(false)
  const [remarksViewModal, setRemarksViewModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [remarks, setRemarks] = useState([])
  const [remarkText, setRemarkText] = useState("")

  //Status
  const [statusList, setStatusList] = useState([])
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const { data } = await axios.get(`${apiURL}/settings/statuses`)
        if (data.status === "success") setStatusList(data.data)
      } catch (err) {
        console.error("Error fetching status list:", err)
      }
    }
    fetchStatuses()
  }, [])

  // Refs and utils
  const selectAllRef = useRef()
  const [dropdownOpen, setDropdownOpen] = useState({}) // Per-row dropdown state

  // Computed values
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      return (
        (!filters.status ||
          lead.status?.toLowerCase() === filters.status?.toLowerCase()) &&
        (!filters.service_need || lead.service_need === filters.service_need) &&
        (!filters.city ||
          lead.city.toLowerCase().includes(filters.city.toLowerCase())) &&
        (!filters.startDate ||
          new Date(lead.created_at) >= new Date(filters.startDate)) &&
        (!filters.endDate ||
          new Date(lead.created_at) <= new Date(filters.endDate))
      )
    })
  }, [leads, filters])

  const selectableLeads = useMemo(() => {
    return filteredLeads.filter(
      l =>
        (l.assigned_to === null || l.assigned_to === undefined) &&
        String(l.status || "").toLowerCase() !== "cancel"
    )
  }, [filteredLeads])

  const allSelected = useMemo(() => {
    return (
      selectableLeads.length > 0 &&
      selectableLeads.every(l => selectedLeadIds.includes(l.id))
    )
  }, [selectableLeads, selectedLeadIds])

  const someSelected = useMemo(() => {
    return selectedLeadIds.length > 0 && !allSelected
  }, [selectedLeadIds, allSelected])

  // Effects
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected
    }
  }, [someSelected])

  useEffect(() => {
    fetchLeads()
    fetchUsers()
  }, [])

  // API Functions
  const fetchLeads = async () => {
    try {
      setLoading(true)
      const { data } = await axios.get(`${apiURL}/leads`)
      if (data.status === "success") setLeads(data.data)
    } catch (err) {
      console.error("Failed to fetch leads:", err)
      toastr.error("Failed to fetch leads")
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${apiURL}/fetchUsers`)
      if (data.status === "success") {
        const allUsers = data.users || []
        const filteredUsers = allUsers.filter(
          u => u.role_ !== "rider" && u.role_ !== "franchises"
        )
        const uniqueCities = [
          ...new Set(filteredUsers.map(u => u.city_?.trim().toUpperCase())),
        ]
        setUsers(filteredUsers)
        setUsersCities(uniqueCities)
      }
    } catch (err) {
      console.error("Error fetching users:", err)
    }
  }

  const fetchRemarks = async leadId => {
    try {
      const { data } = await axios.get(`${apiURL}/remarks/${leadId}`)
      if (data.status === "success") setRemarks(data.data)
    } catch (err) {
      console.error("Error fetching remarks:", err)
    }
  }

  // Handlers
  const handleCheckboxChange = id => {
    setSelectedLeadIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const handleSelectAllChange = isChecked => {
    if (isChecked) {
      setSelectedLeadIds(selectableLeads.map(l => l.id))
    } else {
      setSelectedLeadIds([])
    }
  }

  const handleFormChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async e => {
    e.preventDefault()
    try {
      const { data } = await axios.post(
        `${apiURL}/leads`,
        { ...form, status: "pending" },
        {
          headers: { "x-user-role": localStorage.getItem("role") },
        }
      )
      if (data.status === "success") {
        fetchLeads()
        toastr.success("Lead created successfully")
        setForm({
          name: "",
          contact: "",
          email: "",
          city: "",
          service_need: "",
          notes: "",
        })
        setAddLeadModal(false)
      } else {
        toastr.error(data.message)
      }
    } catch (err) {
      toastr.error("Error creating lead")
      console.error(err)
    }
  }

  const handleStatusChange = async (id, status) => {
    try {
      setStatusLoading(true)
      await axios.post(
        `${apiURL}/leads/${id}/status`,
        { status },
        { headers: { "x-user-role": role } }
      )
      fetchLeads()
      toastr.success("Status updated successfully")
    } catch (err) {
      toastr.error("Failed to update status")
      console.error(err)
    } finally {
      setStatusLoading(false)
    }
  }

  const handleDelete = async id => {
    try {
      await axios.delete(`${apiURL}/leads/${id}`, {
        headers: { "x-user-role": role },
      })
      fetchLeads()
      toastr.success("Lead deleted successfully")
      setDeleteModal(false)
    } catch (err) {
      toastr.error("Failed to delete lead")
      console.error(err)
    }
  }

  const handleAssign = async () => {
    if (!selectedUser) {
      toastr.error("Please select a user to assign")
      return
    }
    const leadIds =
      selectedLeadIds.length > 0
        ? selectedLeadIds
        : singleLeadId
        ? [singleLeadId]
        : []
    if (leadIds.length === 0) {
      toastr.error("No lead selected")
      return
    }
    try {
      setIsAssigning(true)
      const { data } = await axios.post(
        `${apiURL}/leads/assign`,
        {
          lead_ids: leadIds,
          assigned_to: selectedUser,
          assigned_by: assignedBy,
        },
        { headers: { "x-user-role": role } }
      )
      if (data.status === "success") {
        toastr.success("Lead(s) assigned successfully")
        setAssignModalOpen(false)
        setSelectedLeadIds([])
        setSelectedUser(null)
        setSingleLeadId(null)
        fetchLeads()
      } else {
        toastr.error(data.message || "Failed to assign leads")
      }
    } catch (err) {
      toastr.error("Failed to assign leads")
      console.error(err)
    } finally {
      setIsAssigning(false)
    }
  }

  const addRemark = async () => {
    if (!remarkText.trim()) {
      toastr.error("Remark cannot be empty")
      return
    }
    try {
      const { data } = await axios.post(`${apiURL}/remarks`, {
        lead_id: selectedLead.id,
        remark_text: remarkText,
        add_by: assignedBy,
      })
      if (data.status === "success") {
        toastr.success("Remark added successfully")
        setRemarksModal(false)
        setRemarkText("")
        if (remarksViewModal) fetchRemarks(selectedLead.id)
      } else {
        toastr.error(data.message)
      }
    } catch (err) {
      toastr.error("Failed to add remark")
      console.error(err)
    }
  }

  // Modals toggles
  const toggleAddModal = () => setAddLeadModal(!addLeadModal)
  const toggleRemarksModal = lead => {
    setSelectedLead(lead)
    setRemarksModal(true)
    setRemarkText("")
  }
  const toggleViewModal = async lead => {
    setSelectedLead(lead)
    setRemarksViewModal(true)
    await fetchRemarks(lead.id)
  }
  const toggleDeleteModal = lead => {
    setSelectedLead(lead)
    setDeleteModal(true)
  }
  const toggleAssignModal = (leadId = null) => {
    setSingleLeadId(leadId)
    setAssignModalOpen(true)
  }

  // Dropdown toggle per row
  const toggleDropdown = leadId => {
    setDropdownOpen(prev => ({ ...prev, [leadId]: !prev[leadId] }))
  }

  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case "new":
        return "primary"
      case "pending":
        return "warning"
      case "activated":
        return "success"
      case "cancel":
        return "danger"
      default:
        return "secondary"
    }
  }

  const formatDate = dateStr => {
    if (!dateStr) return "----"
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  const formatAssignedTime = timeStr => {
    if (!timeStr) return "--"
    return new Date(timeStr).toLocaleString("en-PK", {
      timeZone: "Asia/Karachi",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  }

  // Columns definition
  const columns = useMemo(
    () => [
      ...(canChangeStatus
        ? [
            {
              Header: () => (
                <Input
                  type="checkbox"
                  innerRef={selectAllRef}
                  checked={allSelected}
                  onChange={e => handleSelectAllChange(e.target.checked)}
                  className="form-check-input"
                  style={{ border: "1px solid black" }}
                />
              ),
              accessor: "select",
              disableFilters: true,
              disableSortBy: true,
              Cell: ({ row }) => {
                const id = row.original.id
                const isChecked = selectedLeadIds.includes(id)
                const isSelectable =
                  (row.original.assigned_to === null ||
                    row.original.assigned_to === undefined) &&
                  String(row.original.status || "").toLowerCase() !== "cancel"
                return (
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    checked={isChecked}
                    onChange={() => handleCheckboxChange(id)}
                    disabled={!isSelectable}
                    style={{ border: "1px solid black" }}
                  />
                )
              },
            },
          ]
        : []),
      {
        Header: "Sr #",
        accessor: "id2",
        className: "fw-semibold",
        disableFilters: true,
        Cell: ({ row }) => <b>{row.index + 1}</b>,
      },
      {
        Header: "Lead ID",
        accessor: "id",
        className: "fw-semibold",
        disableFilters: true,
        Cell: ({ value, row }) => {
          const leadId = row.original.id
          return (
            <Link
              onClick={() => {
                toggleViewModal(row.original)
              }}
            >
              LID-{value}
            </Link>
          )
        },
      },

      {
        Header: "Name",
        accessor: "name",
        className: "fw-semibold",
        disableFilters: true,
      },
      {
        Header: "Contact",
        accessor: "contact",
        disableFilters: true,
        Cell: ({ value }) => (
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-phone text-primary"></i>

            {value || "--"}
          </div>
        ),
      },

      {
        Header: "Email",
        accessor: "email",
        disableFilters: true,
        Cell: ({ value }) => (
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-envelope text-info"></i>

            {value || "--"}
          </div>
        ),
      },
      {
        Header: "City",
        accessor: "city",
        disableFilters: true,
        Cell: ({ value }) => (
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-map text-danger"></i>

            {value || "--"}
          </div>
        ),
      },

      {
        Header: "Service Need",
        accessor: "service_need",
        disableFilters: true,
        Cell: ({ value }) => (
          <Badge className="badge-soft-primary ms-1 font-size-10">
            {value || "----"}
          </Badge>
        ),
      },
      {
        Header: "Created",
        accessor: "created_at",
        disableFilters: true,
        Cell: ({ value }) => (
          <div className="d-flex align-items-center gap-2">
            <i className="bx bx-calendar text-dark"></i>
            {formatDate(value) || "--"}
          </div>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        disableFilters: true,
        Cell: ({ value, row }) => {
          const tooltipId = `status-${row.id}`
          return (
            <div className="d-flex gap-2 align-items-center">
              <Badge
                color={getStatusColor(value)}
                className="text-uppercase px-2 py-1"
              >
                {value}
              </Badge>
              {canChangeStatus && (
                <>
                  <i
                    className="bx bx-edit text-primary"
                    id={tooltipId}
                    onClick={() => toggleViewModal(row.original)}
                    style={{ cursor: "pointer", fontSize: "16px" }}
                  />
                  <UncontrolledTooltip placement="top" target={tooltipId}>
                    Update Status
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          )
        },
      },
      {
        Header: "Assignment Info",
        accessor: "assigned_to",
        disableFilters: true,
        Cell: ({ row, value }) => {
          const tooltipId = `assign-${row.original.id}`
          const lead = row.original
          const user = users.find(u => u.user_id === value)

          if (!value) {
            return (
              <span className="d-flex align-items-center gap-2">
                <Badge color="secondary" className="text-uppercase px-2 py-1">
                  Unassigned
                </Badge>
                {canChangeStatus && (
                  <>
                    <i
                      className="bx bx-user-plus text-primary"
                      id={tooltipId}
                      onClick={() => toggleAssignModal(lead.id)}
                      style={{ cursor: "pointer", fontSize: "16px" }}
                    />
                    <UncontrolledTooltip placement="top" target={tooltipId}>
                      Assign
                    </UncontrolledTooltip>
                  </>
                )}
              </span>
            )
          }

          const formattedTime = formatAssignedTime(lead.assigned_time)
          return (
            <div className="d-flex flex-column align-items-start">
              <div className="d-flex align-items-center gap-2 mb-1">
                <Badge
                  color="success"
                  className="text-uppercase fw-semibold px-2 py-1"
                  id={tooltipId}
                  style={{ cursor: "pointer" }}
                >
                  <i className="fas fa-user-check me-1 fs-6"></i>Assigned
                </Badge>
                <UncontrolledTooltip placement="top" target={tooltipId}>
                  {user ? (
                    <div className="text-start">
                      <strong>{user.full_name}</strong>
                      <br />
                      <small className="text-muted">{user.city_}</small>
                    </div>
                  ) : (
                    "Unknown user"
                  )}
                </UncontrolledTooltip>
              </div>
              <small className="text-muted">{formattedTime}</small>
            </div>
          )
        },
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const leadId = row.original.id
          const isOpen = dropdownOpen[leadId]
          return (
            <div className="position-relative">
              <i
                className="bx bx-dots-vertical-rounded"
                style={{ cursor: "pointer", fontSize: "18px" }}
                onClick={() => toggleDropdown(leadId)}
              />
              {isOpen && (
                <div
                  className="position-absolute bg-white shadow border rounded p-2"
                  style={{
                    top: "30px",
                    right: "0",
                    zIndex: 1050,
                    minWidth: "120px",
                  }}
                >
                  <Button
                    color="link"
                    className="text-primary text-start w-100 p-1 fs-6"
                    onClick={() => {
                      toggleViewModal(row.original)
                      toggleDropdown(leadId)
                    }}
                  >
                    <i className="bx bx-show me-1"></i>View
                  </Button>
                  <Button
                    color="link"
                    className="text-info text-start w-100 p-1 fs-6"
                    onClick={() => {
                      toggleRemarksModal(row.original)
                      toggleDropdown(leadId)
                    }}
                  >
                    <i className="bx bx-message-rounded me-1"></i>Remarks
                  </Button>
                  {canDelete && (
                    <Button
                      color="link"
                      className="text-danger text-start w-100 p-1 fs-6"
                      onClick={() => {
                        toggleDeleteModal(row.original)
                        toggleDropdown(leadId)
                      }}
                    >
                      <i className="bx bx-trash me-1"></i>Delete
                    </Button>
                  )}
                </div>
              )}
            </div>
          )
        },
      },
    ],
    [
      canChangeStatus,
      canDelete,
      allSelected,
      selectedLeadIds,
      selectableLeads,
      users,
      dropdownOpen,
    ]
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-4">
          <Col xs="12">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 fw-semibold text-dark">All Contact Leads</h4>
              {(canChangeStatus || canDelete) && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={toggleAddModal}
                  className="px-3"
                >
                  <i className="bx bx-plus me-1"></i>New Lead
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {/* Filters Accordion */}
        <Accordion
          open={filterOpen ? "1" : ""}
          toggle={() => setFilterOpen(!filterOpen)}
          className="mb-4"
        >
          <AccordionItem>
            <AccordionHeader
              targetId="1"
              className="fw-semibold bg-light rounded"
            >
              <i className="bx bx-filter-alt me-2"></i>Filters
            </AccordionHeader>
            <AccordionBody accordionId="1">
              <div className="p-3 bg-light rounded">
                <Row className="g-3">
                  <Col md={2}>
                    <Label className="form-label fw-medium">Status</Label>
                    <Input
                      type="select"
                      value={filters.status}
                      onChange={e =>
                        setFilters({ ...filters, status: e.target.value })
                      }
                      className="form-select-sm"
                    >
                      <option value="">All</option>
                      {statusList.map(status => (
                        <option key={status.id} value={status.status_name}>
                          {status.status_name.charAt(0).toUpperCase() +
                            status.status_name.slice(1)}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Label className="form-label fw-medium">Service Need</Label>
                    <Input
                      type="select"
                      value={filters.service_need}
                      onChange={e =>
                        setFilters({ ...filters, service_need: e.target.value })
                      }
                      className="form-select-sm"
                    >
                      <option value="">All</option>
                      <option value="COD">COD</option>
                      <option value="Courier">Courier</option>
                      <option value="International">International</option>
                      <option value="Warehouse">Warehouse</option>
                      <option value="MOFA">MOFA</option>
                      <option value="Apostille">Apostille</option>
                      <option value="Franchise">Franchise</option>
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Label className="form-label fw-medium">City</Label>
                    <Input
                      type="text"
                      placeholder="Search city"
                      value={filters.city}
                      onChange={e =>
                        setFilters({ ...filters, city: e.target.value })
                      }
                      className="form-control-sm"
                    />
                  </Col>
                  <Col md={2}>
                    <Label className="form-label fw-medium">From</Label>
                    <div className="position-relative">
                      <DatePicker
                        selected={filters.startDate}
                        onChange={date =>
                          setFilters({ ...filters, startDate: date })
                        }
                        placeholderText="Start date"
                        dateFormat="yyyy-MM-dd"
                        className="form-control form-control-sm ps-4"
                        isClearable
                      />{" "}
                      <i className="bx bx-calendar position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"></i>
                    </div>
                  </Col>
                  <Col md={2}>
                    <Label className="form-label fw-medium">To</Label>
                    <div className="position-relative">
                      <DatePicker
                        selected={filters.endDate}
                        onChange={date =>
                          setFilters({ ...filters, endDate: date })
                        }
                        placeholderText="End date"
                        dateFormat="yyyy-MM-dd"
                        className="form-control form-control-sm ps-4"
                        isClearable
                      />
                      <i className="bx bx-calendar position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"></i>
                    </div>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      color="outline-secondary"
                      size="sm"
                      onClick={() =>
                        setFilters({
                          status: "",
                          service_need: "",
                          city: "",
                          startDate: null,
                          endDate: null,
                        })
                      }
                      className="w-100"
                    >
                      <i className="bx bx-refresh me-1"></i>Clear
                    </Button>
                  </Col>
                </Row>
              </div>
            </AccordionBody>
          </AccordionItem>
        </Accordion>

        {/* Table Card */}
        <Card>
          <CardBody>
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" className="mb-3" />
                <p className="text-muted">Loading leads...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <div className="float-end d-flex gap-2">
                  {selectedLeadIds.length > 0 && canChangeStatus && (
                    <Button
                      color="dark"
                      className="btn btn-sm"
                      onClick={() => setAssignModalOpen(true)}
                    >
                      Assign Leads ({selectedLeadIds.length})
                    </Button>
                  )}
                  {(canChangeStatus || canDelete) && (
                    <ExcelDownload leads={leads} title="Download Leads" />
                  )}
                </div>
                <TableContainer
                  columns={columns}
                  data={filteredLeads}
                  isGlobalFilter={true}
                  customPageSize={10}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* Add Lead Modal */}
        <Modal
          isOpen={addLeadModal}
          toggle={toggleAddModal}
          centered
          size="md"
          className="modal-dialog-centered"
        >
          <ModalHeader
            toggle={toggleAddModal}
            className="bg-light border-bottom"
          >
            <h5 className="mb-0 fw-semibold">Create New Lead</h5>
          </ModalHeader>
          <Form onSubmit={handleSubmit}>
            <ModalBody className="p-4">
              <Row className="g-3">
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label fw-medium">
                      Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="Full Name"
                      name="name"
                      value={form.name}
                      onChange={handleFormChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label fw-medium">
                      Contact <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="contact"
                      placeholder="Contact"
                      value={form.contact}
                      onChange={handleFormChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label fw-medium">
                      Email <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={form.email}
                      onChange={handleFormChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label fw-medium">
                      City <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="City"
                      name="city"
                      value={form.city}
                      onChange={handleFormChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label className="form-label fw-medium">
                      Service Need <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      name="service_need"
                      value={form.service_need}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select</option>
                      <option value="COD">COD</option>
                      <option value="International">International</option>
                      <option value="Courier">Courier</option>
                      <option value="Warehouse">Warehouse</option>
                      <option value="MOFA">MOFA</option>
                      <option value="Apostille">Apostille</option>
                      <option value="Franchise">Franchise</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label className="form-label fw-medium">Notes</Label>
                    <Input
                      type="textarea"
                      name="notes"
                      value={form.notes}
                      onChange={handleFormChange}
                      rows="3"
                      placeholder="Additional notes..."
                    />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter className="bg-light border-top">
              <Button type="submit" color="primary" size="sm" className="px-4">
                Create Lead
              </Button>
              <Button
                type="button"
                color="secondary"
                size="sm"
                onClick={toggleAddModal}
                className="px-4"
              >
                Cancel
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* Assign Modal */}
        <Modal
          isOpen={assignModalOpen}
          toggle={() => setAssignModalOpen(false)}
          centered
        >
          <ModalHeader
            toggle={() => setAssignModalOpen(false)}
            className="bg-light"
          >
            <div className="d-flex align-items-center">
              <i className="fas fa-user-plus text-primary me-2"></i>
              Assign{" "}
              {selectedLeadIds.length > 0
                ? `${selectedLeadIds.length} Leads`
                : `Lead ${singleLeadId}`}
            </div>
          </ModalHeader>
          <ModalBody className="p-4">
            <Alert color="warning" className="d-flex align-items-center">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Assigning{" "}
              {selectedLeadIds.length > 0
                ? `${selectedLeadIds.length} selected leads`
                : `lead ID: ${singleLeadId}`}{" "}
              to selected user.
            </Alert>
            <FormGroup>
              <Label className="form-label fw-medium">
                Select User <span className="text-danger">*</span>
              </Label>
              <Select
                value={
                  users
                    .map(u => ({
                      value: u.user_id,
                      label: `${u.role_.substring(0, 2).toUpperCase()}: ${
                        u.full_name
                      } (${u.city_})`,
                    }))
                    .find(opt => opt.value === selectedUser) || null
                }
                onChange={opt => setSelectedUser(opt?.value || null)}
                options={users.map(u => ({
                  value: u.user_id,
                  label: `${u.role_.substring(0, 2).toUpperCase()}: ${
                    u.full_name
                  } (${u.city_})`,
                }))}
                placeholder="Select user..."
                isClearable
                isSearchable
                noOptionsMessage={() => "No users available"}
                className="react-select-sm"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button
              color="secondary"
              size="sm"
              onClick={() => setAssignModalOpen(false)}
              disabled={isAssigning}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={handleAssign}
              disabled={isAssigning || !selectedUser}
            >
              {isAssigning ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Assigning...
                </>
              ) : (
                "Assign"
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Remarks Modal */}
        <Modal
          isOpen={remarksModal}
          toggle={() => setRemarksModal(false)}
          centered
        >
          <ModalHeader toggle={() => setRemarksModal(false)}>
            Add Remark for Lead #{selectedLead?.id}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Remark</Label>
              <Input
                type="textarea"
                rows="4"
                value={remarkText}
                onChange={e => setRemarkText(e.target.value)}
                placeholder="Enter remark..."
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" size="sm" onClick={addRemark}>
              Submit
            </Button>
            <Button
              color="secondary"
              size="sm"
              onClick={() => setRemarksModal(false)}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        {/* View Remarks Modal */}
        <Modal
          isOpen={remarksViewModal}
          toggle={() => setRemarksViewModal(false)}
          size="lg"
          centered
          className="modal-clean"
          scrollable
        >
          <ModalHeader
            toggle={() => setRemarksViewModal(false)}
            className="border-bottom bg-light"
          >
            <h5 className="mb-0 fw-semibold text-dark">
              <i className="bx bx-detail me-2 text-primary"></i>
              Lead Details - LID-{selectedLead?.id}
            </h5>
          </ModalHeader>
          <ModalBody className="p-0">
            {selectedLead && (
              <>
                {/* Lead Details Card */}
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-primary text-white py-2">
                    <h6 className="mb-0 fw-semibold">
                      <i className="bx bx-user me-1"></i>Lead Information
                    </h6>
                  </div>
                  <div className="card-body p-3">
                    <dl className="row mb-0 g-2">
                      {[
                        {
                          label: "Name",
                          value: selectedLead.name,
                          icon: "bx-user",
                        },
                        {
                          label: "Contact",
                          value: selectedLead.contact,
                          icon: "bx-phone",
                        },
                        {
                          label: "Email",
                          value: selectedLead.email,
                          icon: "bx-envelope",
                        },
                        {
                          label: "City",
                          value: selectedLead.city,
                          icon: "bx-map",
                        },
                        {
                          label: "Service Need",
                          value: selectedLead.service_need,
                          icon: "bx-package",
                        },
                        {
                          label: "Notes",
                          value: selectedLead.notes,
                          icon: "bx-note",
                        },
                      ].map(({ label, value, icon }) => (
                        <React.Fragment key={label}>
                          <dt className="col-sm-4 text-muted small mb-1 d-flex align-items-center">
                            <i
                              className={`bx ${icon} me-2 text-primary fs-5`}
                            ></i>
                            {label}
                          </dt>
                          <dd className="col-sm-8 text-dark fw-medium small mb-1">
                            {value || <span className="text-muted">----</span>}
                          </dd>
                        </React.Fragment>
                      ))}
                      <dt className="col-sm-4 text-muted small mb-1 d-flex flex-column align-items-start">
                        <div className="d-flex align-items-center mb-1">
                          <i className="bx bx-badge-check me-2 text-success fs-5"></i>
                          Status
                        </div>
                        <Badge
                          pill
                          color={getStatusColor(selectedLead.status)}
                          className="py-1 fw-semibold text-uppercase"
                        >
                          {selectedLead.status}
                        </Badge>
                      </dt>
                      <dd className="col-sm-8">
                        {canChangeStatus ? (
                          statusLoading ? (
                            <div className="d-flex align-items-center gap-2">
                              <Spinner
                                size="sm"
                                color="primary"
                                style={{ width: "1rem", height: "1rem" }}
                              />
                              <small className="text-muted">Updating...</small>
                            </div>
                          ) : (
                            <select
                              className="form-select form-select-sm"
                              style={{ width: "15rem", height: "2rem" }}
                              value={selectedLead.status}
                              onChange={async e => {
                                await handleStatusChange(
                                  selectedLead.id,
                                  e.target.value
                                )
                                setRemarksViewModal(false)
                              }}
                            >
                              {statusList.map(status => (
                                <option
                                  key={status.id}
                                  value={status.status_name}
                                >
                                  {status.status_name.charAt(0).toUpperCase() +
                                    status.status_name.slice(1)}
                                </option>
                              ))}
                            </select>
                          )
                        ) : (
                          <Badge
                            color={getStatusColor(selectedLead.status)}
                            className="px-3 py-2 fw-semibold text-uppercase"
                          >
                            {selectedLead.status}
                          </Badge>
                        )}
                      </dd>
                    </dl>
                  </div>
                </div>

                {/* Assignment Info Card - Only if assigned */}
                {selectedLead.assigned_to && (
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-header bg-success text-white py-2">
                      <h6 className="mb-0 fw-semibold">
                        <i className="fas fa-user-check me-1"></i>Assignment
                        Details
                      </h6>
                    </div>
                    <div className="card-body p-3">
                      <dl className="row mb-0 g-2">
                        <dt className="col-sm-4 text-muted small mb-1 d-flex align-items-center">
                          <i className="fas fa-user-tie me-2 text-info fs-5"></i>
                          Assigned To
                        </dt>
                        <dd className="col-sm-8 text-dark fw-medium small mb-1">
                          {(() => {
                            const assignee = users.find(
                              u => u.user_id === selectedLead.assigned_to
                            )
                            return assignee ? (
                              <div>
                                <div className="fw-semibold">
                                  {assignee.full_name}
                                </div>
                                <small className="text-muted">
                                  {assignee.city_ || "N/A"}
                                </small>
                              </div>
                            ) : (
                              <span className="text-muted">Unknown User</span>
                            )
                          })()}
                        </dd>
                        <dt className="col-sm-4 text-muted small mb-1 d-flex align-items-center">
                          <i className="bx bx-user-check me-2 text-warning fs-5"></i>
                          Assigned By
                        </dt>
                        <dd className="col-sm-8 text-dark fw-medium small mb-1">
                          {(() => {
                            const assigner = users.find(
                              u => u.user_id === selectedLead.assigned_by
                            )
                            return assigner ? (
                              <div>
                                <div className="fw-semibold">
                                  {assigner.full_name}
                                </div>
                                <small className="text-muted">
                                  {assigner.city_ || "N/A"}
                                </small>
                              </div>
                            ) : (
                              <span className="text-muted">Unknown User</span>
                            )
                          })()}
                        </dd>
                        <dt className="col-sm-4 text-muted small mb-1 d-flex align-items-center">
                          <i className="bx bx-time-five me-2 text-secondary fs-5"></i>
                          Assigned At
                        </dt>
                        <dd className="col-sm-8 text-dark fw-medium small mb-1">
                          {formatAssignedTime(selectedLead.assigned_time) ||
                            "N/A"}
                        </dd>
                      </dl>
                    </div>
                  </div>
                )}

                {/* Remarks Section */}
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-info text-white py-2">
                    <h6 className="mb-0 fw-semibold">
                      <i className="bx bx-message-rounded me-1"></i>Remarks
                      History
                      {remarks.length > 0 && (
                        <Badge color="light" className="ms-2">
                          {remarks.length}
                        </Badge>
                      )}
                    </h6>
                  </div>
                  <div className="card-body p-0">
                    {remarks.length === 0 ? (
                      <div className="text-center py-4 bg-light rounded-bottom">
                        <i
                          className="fas fa-comment-slash text-muted mb-2"
                          style={{ fontSize: "3rem" }}
                        ></i>
                        <p className="text-muted mb-0">No remarks added yet.</p>
                      </div>
                    ) : (
                      <div
                        className="table-responsive"
                        style={{ maxHeight: "400px", overflowY: "auto" }}
                      >
                        <table className="table table-sm mb-0 align-middle">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th className="ps-3">#</th>
                              <th className="w-50">Remark</th>
                              <th>Added By</th>
                              <th className="text-end pe-3">Date & Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {remarks.map((r, i) => {
                              const user = users.find(
                                u => u.user_id === r.add_by
                              )
                              return (
                                <tr key={r.id} className="border-bottom">
                                  <td className="ps-3 text-muted small fw-medium">
                                    {i + 1}
                                  </td>
                                  <td className="small text-dark">
                                    {r.remark_text}
                                  </td>
                                  <td className="small">
                                    <div className="fw-semibold text-info">
                                      {user?.full_name || "Unknown"}
                                    </div>
                                    <small className="text-muted d-block">
                                      {user?.city_ || "N/A"}
                                    </small>
                                  </td>
                                  <td className="text-end pe-3 text-muted small">
                                    {new Date(r.created_at).toLocaleString(
                                      "en-PK",
                                      {
                                        timeZone: "Asia/Karachi",
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: true,
                                      }
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter className="bg-light border-top justify-content-start">
            {canChangeStatus && (
              <Button
                color="primary"
                size="sm"
                className="px-3"
                onClick={() => {
                  setRemarksModal(true)
                  setRemarksViewModal(false)
                }}
              >
                <i className="bx bx-plus me-1"></i>Add Remark
              </Button>
            )}
            <div className="ms-auto">
              <Button
                color="secondary"
                size="sm"
                onClick={() => setRemarksViewModal(false)}
              >
                Close
              </Button>
            </div>
          </ModalFooter>
        </Modal>

        {/* Delete Modal */}
        <Modal
          isOpen={deleteModal}
          toggle={() => setDeleteModal(false)}
          centered
        >
          <ModalHeader toggle={() => setDeleteModal(false)}>
            Confirm Delete
          </ModalHeader>
          <ModalBody>
            Are you sure you want to delete Lead{" "}
            <strong>#{selectedLead?.id}</strong>?
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              size="sm"
              onClick={() => handleDelete(selectedLead.id)}
            >
              Delete
            </Button>
            <Button
              color="secondary"
              size="sm"
              onClick={() => setDeleteModal(false)}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  )
}

export default AllContactLeads
